import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import {
  syncTeamsTreeSelector,
  syncTeamsRootLevelTreeSelector,
  selectedTeamWithExtraDataSelector,
  teamsFlatTreeSelector
} from 'state/selectors/teams';
import MultiLevelDropdown from 'components/base/MultiLevelDropdown';
import TreeTeamsSelected from './TreeTeamsSelected';
import TreeTeamsNoSearchResults from './TreeTeamsNoSearchResults';
import {
  addDefaultTeamIdsToTreeOptions,
  formatTreeForDropdown,
  filterFromTree,
  getOptionActions
} from './TreeTeamsDropdown.utils';
import useConfirmModal from 'components/hooks/useConfirmModal';
import useTreeTeamsSetting from './useTreeTeamsSetting';
import { allTeams } from 'connected-components/TopBar/TopBar.service';
import { shouldShowAllTeamsModeSelector } from 'state/selectors/ui-state';
import { getAllTeamsGroup, getTreeWhenAllTeamsGroup } from 'common/utils/utils';
import { useMemo } from 'react';

/**
 * TreeTeamDropdown props
 */
type TreeTeamDropdownProps = Record<string, unknown> & {
  disabled?: boolean;
  defaultShownTeamIds?: number[];
  selectedTeamIds: number[];
  customAllItem?: any;
  size?: string;
  onChange: (selected: unknown) => void;
};

/**
 * Team picker component
 */
const TreeTeamsDropdown = (props: TreeTeamDropdownProps) => {
  const {
    disabled,
    disabledDropdown = false,
    selectedTeamIds = [],
    defaultShownTeamIds = [],
    disabledTeamIds = [],
    onChange,
    multiple,
    multipleLimit,
    width,
    height,
    size, // options are: small/medium/large. defines text and avatar size and default height
    menuWidth,
    menuWidthExpanded,
    menuHeight,
    numOfMenus,
    isSelectRootEnabled,
    isAllowNoDefault = false,
    isUncheckDefault = false,
    isFilterTreeBySelectedTeam = false,
    isFilterTreeBySelectRoot = true,
    isDisableChildrenOfSelectedParent = false,
    isShowAllTeams: isShowAllTeamsProp = true,
    customAllItem = allTeams,
    isOpenParentsOfSelected = false,
    isInline = false,
    isShowGroupsOnly = false,
    isShowTopLevelOnly = false,
    isHideSynced = false,
    isHideAllContributors = false,
    isHideAllTeamsGroup = false,
    customPlaceholder = null,
    isGroupSelectionDisabled = false,
    dataCyPrefix = ''
  } = props;
  const { rootTeamId, addRootTeamToFavorites, removeRootTeamFromFavorites } =
    useTreeTeamsSetting();
  const confirmModal = useConfirmModal();
  // TO DO: when all teams group is GA and RBAC is GA, adjust shouldShowAllTeamsMode
  const shouldShowAllTeamsMode = useSelector(shouldShowAllTeamsModeSelector);
  const flatTeams = useSelector(teamsFlatTreeSelector);

  const tree =
    useSelector(
      isFilterTreeBySelectedTeam
        ? selectedTeamWithExtraDataSelector
        : isFilterTreeBySelectRoot
        ? syncTeamsRootLevelTreeSelector
        : syncTeamsTreeSelector
    ) || [];

  const allTeamsGroup = getAllTeamsGroup(tree);
  const isAllTeamsGroup = !isEmpty(allTeamsGroup);
  // TO DO: when all teams group is GA, delete customAllItem
  const customAllItemNew = isAllTeamsGroup ? allTeamsGroup : customAllItem;
  const shouldShowAllTeams =
    isShowAllTeamsProp && shouldShowAllTeamsMode && !isAllTeamsGroup;

  let treeTeams = useMemo(() => structuredClone(tree), [tree]);
  if (isShowGroupsOnly || isHideSynced || isHideAllContributors) {
    treeTeams = filterFromTree({
      treeTeams,
      isShowGroupsOnly,
      isHideSynced,
      isHideAllContributors
    });
  }

  // when we are in all teams group mode
  if (!!treeTeams.length && isAllTeamsGroup) {
    treeTeams = getTreeWhenAllTeamsGroup(treeTeams);
  }

  if (isShowTopLevelOnly) {
    treeTeams = treeTeams.map(item => {
      return { ...item, children: [] };
    });
  }

  const isShowSearchResultsAction = isSelectRootEnabled && rootTeamId;
  const isAllTeamsInTree =
    shouldShowAllTeams && !rootTeamId && !isFilterTreeBySelectedTeam;

  // TO DO: when all teams group is GA, delete customAllItem here
  let treeOptions = formatTreeForDropdown({
    arr: treeTeams,
    nestingKey: 'children',
    size,
    allTeamsTeam: isAllTeamsInTree ? customAllItem : null,
    allTeamsGroup,
    isHideAllTeamsGroup,
    isGroupSelectionDisabled
  });

  if (defaultShownTeamIds.length > 0) {
    treeOptions = addDefaultTeamIdsToTreeOptions({
      flatTeams,
      defaultShownTeamIds,
      isShowTopLevelOnly,
      size,
      treeOptions
    });
  }
  // TO DO: when all teams group is GA, delete customAllItem here
  const defaultTeamId = isAllTeamsInTree
    ? customAllItem?.id
    : treeOptions?.[0]?.value;

  const getSelectedTeams = () => {
    if (isUncheckDefault) {
      return [];
    }

    if (!selectedTeamIds.length || !selectedTeamIds?.[0]) {
      return isAllowNoDefault ? [] : [defaultTeamId];
    }

    return multiple ? selectedTeamIds : [selectedTeamIds[0]];
  };
  const selected = getSelectedTeams();

  return treeOptions.length ? (
    <MultiLevelDropdown
      // @ts-expect-error
      disabledDropdown={disabledDropdown}
      tree={treeOptions}
      selected={selected}
      disabled={disabledTeamIds || disabled}
      disabledMode={disabled}
      dataAttrName={`${dataCyPrefix ? dataCyPrefix + '-team' : 'team'}`}
      isInline={isInline}
      onChange={(selectedTeamsIds = []) => {
        const isCustomAllOptionSelected =
          selectedTeamsIds[0] === customAllItem.id;
        // get selected teams objects from ids and sent to onChange
        // if no selectedTeamsIds and !isAllowNoDefault - return defaultTeam object. if isAllowNoDefault - return []
        const selectedTeamsByIds = flatTeams.filter(({ id }) =>
          selectedTeamsIds.length === 0 && !isAllowNoDefault
            ? defaultTeamId === id
            : selectedTeamsIds.includes(id)
        );
        const selectedTeams = isCustomAllOptionSelected
          ? [customAllItem.id]
          : selectedTeamsByIds;
        onChange(selectedTeams);
      }}
      renderSelected={selected => (
        <TreeTeamsSelected
          teams={selected.length ? selected : [treeOptions[0]]}
          size={size}
          disabled={disabled}
          customPlaceholder={customPlaceholder}
        />
      )}
      renderSelectedNotFound={(selectedIds, selectedIdsNotInTree) => {
        // get selected teams objects from ids
        // if team not in dropdown, set isNotFound to true
        const selectedTeams = flatTeams
          .concat(customAllItemNew)
          .filter(({ id }) => selectedIds.includes(id))
          .map(item => ({
            ...item,
            origin: {
              name: item.name,
              isNotFound: selectedIdsNotInTree.includes(item.id)
            }
          }));

        return (
          <TreeTeamsSelected
            teams={selectedTeams}
            size={size}
            teamsNotFound
            customPlaceholder={customPlaceholder}
          />
        );
      }}
      multiple={multiple}
      multipleLimit={multipleLimit}
      isDisableChildrenOfSelectedParent={isDisableChildrenOfSelectedParent}
      isOpenParentsOfSelected={isOpenParentsOfSelected}
      searchable
      width={width}
      height={height}
      size={size}
      menuWidth={menuWidth}
      menuWidthExpanded={menuWidthExpanded}
      menuHeight={menuHeight}
      numOfMenus={numOfMenus}
      customAllItem={customAllItemNew}
      noSearchResultsAction={
        isShowSearchResultsAction ? (
          <TreeTeamsNoSearchResults
            removeRootTeamFromFavorites={removeRootTeamFromFavorites}
          />
        ) : (
          <></>
        )
      }
      optionActions={getOptionActions({
        rootTeamId,
        isSelectRootEnabled,
        defaultTeamId,
        onChange,
        addRootTeamToFavorites,
        removeRootTeamFromFavorites,
        confirmModal
      })}
    />
  ) : null;
};

export default TreeTeamsDropdown;
